import { Box, Typography, TablePagination } from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { useTranslation } from "react-i18next";
import useFetch from "hooks/useFetch";
import { getToken } from "utils/getToken";
import { useCallback, useEffect, useRef, useState } from "react";
import { PurchasedDomain } from "interfaces/domains";
import { debounce } from "utils/debounce";
import SearchBar from "components/SearchBar";
import PurchasedDomainsTab from "components/PurchasedDomainsTab";
import useMediaByValue from "hooks/useMediaByValue";
import useCautionStore from "store/cautionStore";
import CautionModal from "components/CautionModal";
import { toast } from "react-toastify";

interface UserStats {
  userId: string;
  email: string;
  telegram: string;
  totalDomains: number;
  totalUserSpent: number;
  totalCost: number;
}

const PurchasedDomainsPage = () => {
  const { t } = useTranslation();
  const token = getToken();
  const [purchasedDomains, setPurchasedDomains] = useState<Array<PurchasedDomain>>([]);
  const [totalPurchasedDomains, setTotalPurchasedDomains] = useState<number>(0);
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [page, setPage] = useState<number>(0);
  const [filter, setFilter] = useState<string>("");
  const { closeModal, handleDomainToDeleteChange, isDomainDeleteModalOpen, domainToDelete } = useCautionStore();
  const abortControllerRef = useRef<AbortController | null>(null);
  const rowsPerPage = 20;
  const isLargeViewPort = useMediaByValue("(min-width:1120px)");

  const { triggerFetch: getPurchasedDomains } = useFetch({
    url: "admin/purchased-domains",
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const { loading: domainDeletionLoading, triggerFetch: deletePurchasedDomain } = useFetch({
    url: `admin/purchased-domains/${domainToDelete?.id}`,
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const buildQueryParams = (page: number, filter: string) => {
    const params = new URLSearchParams();
    params.append("page", (page + 1).toString());
    params.append("limit", rowsPerPage.toString());

    if (filter) {
      params.append("searchTerm", filter);
    }

    return params.toString();
  };

  const handleGetPurchasedDomains = useCallback(
    async (currentPage: number, currentFilter: string) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const queryParams = buildQueryParams(currentPage, currentFilter);
        const response = await getPurchasedDomains(undefined, `admin/purchased-domains?${queryParams}`, {
          signal: controller.signal,
        });

        if (!controller.signal.aborted && response?.success) {
          setPurchasedDomains(response.purchasedDomains || []);
          setTotalPurchasedDomains(response.pagination.total || 0);
          if (response.userStats) {
            setUserStats(response.userStats);
          } else {
            setUserStats(null);
          }
        }
      } catch (error: any) {
        if (error?.name !== "AbortError") {
          console.error("Error fetching purchased domains:", error);
        }
      }
    },
    [getPurchasedDomains]
  );

  const handleGetPurchasedDomainsRef = useRef(handleGetPurchasedDomains);

  useEffect(() => {
    handleGetPurchasedDomainsRef.current = handleGetPurchasedDomains;
  }, [handleGetPurchasedDomains]);

  const debouncedSearchRef = useRef(
    debounce((value: string) => {
      void handleGetPurchasedDomainsRef.current(0, value);
    }, 500)
  );

  const handleSearch = (value: string) => {
    setFilter(value);
    setPage(0);
    debouncedSearchRef.current(value);
  };

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number): void => {
    setPage(newPage);
    void handleGetPurchasedDomains(newPage, filter);
  };

  const handlePurchasedDomainDelete = async () => {
    try {
      await deletePurchasedDomain();
      await handleGetPurchasedDomains(page, filter);
      handleDomainToDeleteChange(null);
      closeModal();
      toast.success(t("successDomainDeletion"));
    } catch {
      toast.error(t("unsuccessfulDomainDeletion"));
    }
  };

  useEffect(() => {
    void handleGetPurchasedDomains(0, filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: isLargeViewPort ? "row" : "column",
          justifyContent: "space-between",
          alignItems: isLargeViewPort ? "center" : "flex-start",
          marginBottom: "30px",
        }}
      >
        <Box sx={{ display: "flex", gap: "6px", alignItems: "center", marginBottom: isLargeViewPort ? 0 : "30px" }}>
          <ShoppingCartIcon sx={{ width: "36px", height: "36px", color: "#6D6777" }} />
          <Typography
            sx={{
              fontFamily: "var(--cygreRegular), sans-serif",
              fontSize: "20px",
              fontWeight: 500,
              color: "#2E263D",
            }}
          >
            {t("purchasedDomains")}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: isLargeViewPort ? "auto" : "100%",
          }}
        >
          <Box sx={{ display: "flex", gap: "6px" }}>
            <SearchBar
              useSearchValue={true}
              searchValue={filter}
              onSearch={handleSearch}
              placeholder={t("search")}
              customStyles={{
                fontSize: 14,
                boxShadow:
                  "rgba(255, 255, 255, 0.5) 2px 2px 2px 0px inset, rgba(0, 0, 0, 0.1) 7px 7px 20px 0px, rgba(0, 0, 0, 0.1) 4px 4px 5px 0px",
                "& svg": { width: "20px", height: "20px" },
              }}
            />
          </Box>
          {!isLargeViewPort && (
            <TablePagination
              rowsPerPageOptions={[]}
              component="div"
              count={totalPurchasedDomains}
              rowsPerPage={20}
              page={page}
              labelRowsPerPage={null}
              labelDisplayedRows={() => ""}
              onPageChange={handleChangePage}
              sx={{
                overflow: "hidden",
                "& .MuiTablePagination-spacer": {
                  display: "none",
                },
                "& .MuiTablePagination-toolbar": {
                  padding: 0,
                  justifyContent: "flex-start",
                },
                "& .MuiTablePagination-actions": {
                  margin: "0px !important",
                },
              }}
            />
          )}
        </Box>
        {isLargeViewPort && (
          <TablePagination
            rowsPerPageOptions={[]}
            component="div"
            count={totalPurchasedDomains}
            rowsPerPage={20}
            page={page}
            labelRowsPerPage={null}
            labelDisplayedRows={() => ""}
            onPageChange={handleChangePage}
            sx={{
              "& .MuiTablePagination-spacer": {
                display: "none",
              },
              "& .MuiTablePagination-toolbar": {
                padding: 0,
                justifyContent: "flex-start",
              },
            }}
          />
        )}
      </Box>
      <PurchasedDomainsTab
        totalDomains={totalPurchasedDomains}
        domainsArr={purchasedDomains}
        page={page}
        handleChangePage={handleChangePage}
        userStats={userStats}
      />
      {isDomainDeleteModalOpen && (
        <CautionModal handleConfirm={handlePurchasedDomainDelete} isLoading={domainDeletionLoading} />
      )}
    </>
  );
};

export default PurchasedDomainsPage;
